import { useState, useRef, useContext, useMemo, useEffect } from "react";
import { Button, Modal } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import _ from "lodash";

import cx from "classnames";

import {
  creatingPersonalizationLead,
  creatingPersonalizationLeadFailed,
} from "actions/personalization";
import StoryboardContext from "components/Storyboard/context";

import { FailureToast, SuccessToast } from "components/Toast";
import { toast } from "react-toastify";

import styles from "./csvUpload.module.scss";
import TruncatedTextWithTooltip from "components/TruncatedTextWithTooltip";

const headerMap = {
  email: "email",
  firstname: "firstName",
  lastname: "lastName",
  workphone: "workPhone",
  title: "title",
  company: "company",
};

const publicDomains = [
  "gmail.com",
  "yahoo.com",
  "outlook.com",
  "hotmail.com",
  "aol.com",
  "icloud.com",
  "protonmail.com",
  "live.com",
];

function CsvUpload({ onClose, handleFetchLeads }) {
  const storyboardContext = useContext(StoryboardContext);
  const storyboardId = useSelector((state) =>
    _.get(
      state,
      "storyboards.storyboard.item.id",
      _.get(storyboardContext, "id")
    )
  );
  const creating = useSelector((state) =>
    _.get(state, `personalization.leads.creating`, false)
  );
  const error = useSelector((state) =>
    _.get(state, `personalization.leads.error`, false)
  );

  const [csvFile, setCsvFile] = useState({
    list: [],
    totalRowsCount: 0,
    error: "",
    fileName: "",
  });

  const uploadedCountRef = useRef(0);

  const dispatch = useDispatch();

  const filteredList = useMemo(() => {
    return csvFile.list.filter((item) => item.emailError !== true);
  }, [csvFile.list]);

  const isUploaded = csvFile.list.length > 0;

  const isMoreThanHalf =
    filteredList.length === 1 && csvFile.totalRowsCount === 1
      ? true
      : csvFile.totalRowsCount / filteredList.length < 2;

  let uploadingFilesCount = Math.ceil(filteredList.length / 25);

  useEffect(() => {
    if (!error) return;
    toast(
      <FailureToast
        heading={`Upload Failed`}
        description={"Unable to upload the CSV file"}
      />,
      {
        position: toast.POSITION.BOTTOM_RIGHT,
        autoClose: 3000,
      }
    );
    dispatch(creatingPersonalizationLeadFailed(false));
    onClose();
  }, [error]);

  useEffect(() => {
    if (!isMoreThanHalf && isUploaded) {
      setCsvFile({
        ...csvFile,
        error: "INVALID_ROWS",
      });
    }
  }, [isUploaded]);

  const handleImport = (uploadedCount) => {
    try {
      if (uploadedCount < 0) {
        toast(
          <SuccessToast
            heading={`Success`}
            description={`${filteredList.length} Leads imported`}
          />,
          {
            position: toast.POSITION.BOTTOM_RIGHT,
            autoClose: 3000,
          }
        );

        handleFetchLeads(0);
        onClose();
        return;
      }

      const startIndex =  uploadedCount - 1 < 0 ? 0 : (uploadedCount - 1) * 25;
      const endIndex = startIndex + 25;
      const filesToUpload = filteredList.slice(startIndex, endIndex).reverse();

      const isCsvFile = uploadedCount > 0; // Last CSV uploading counts considered as a false to set leads as empty []

      if (uploadedCountRef.current)
        uploadedCountRef.current.textContent = Math.min(filteredList.length - startIndex, filteredList.length);

      dispatch(
        creatingPersonalizationLead(
          storyboardId,
          filesToUpload,
          isCsvFile,
          () => {
            handleImport(uploadedCount - 1);
          }
        )
      );
    } catch (err) {
      console.log({ err });
    }
  };

  return (
    <Modal
      className={styles.csvUploadModal}
      contentClassName={styles.csvUploadModalContent}
      dialogClassName={styles.csvUploadModalDialog}
      backdropClassName={styles.backdropWrapper}
      show={true}
      centered
      size="sm"
      onClick={(e) => {
        e.stopPropagation();
      }}
    >
      <Modal.Header className={styles.csvUploadModalHeaderWrapper}>
        <div className={styles.csvUploadModalHeader}>
          <p>Import Leads</p>
          <Button
            variant="info"
            onClick={onClose}
            className={styles.closeButton}
          >
            <i className={cx(styles.icon, "cleverstory-close")} />
          </Button>
        </div>
        <p>
          {(csvFile.error === "INVALID_ROWS" ||
            csvFile.error === "INVALID_EMAILS") && (
            <span>
              Spot and verify imported leads before adding it to your list. 
            </span>
          )}
          {!isUploaded && <span>Maximum 100 leads</span>}
        </p>
      </Modal.Header>
      <Modal.Body
        className={cx(styles.csvUploadModalBody, {
          [styles.csvafterUploadModalBody]: !!isUploaded,
        })}
      >
        {isUploaded && (
          <div className={styles.listWrapper}>
            {csvFile.list.map((item) => {
              return (
                <div className={styles.list}>
                  <p className={cx({ [styles.error]: item.emailError })}>
                    {item.email}
                  </p>
                </div>
              );
            })}
          </div>
        )}
        {!isUploaded && (
          <>
            <p className={styles.uploadText}>
              <span>Upload your CSV file</span>
              {(csvFile.error === "FILE_TYPE_ERROR" ||
                csvFile.error === "MAX_ROWS_ERROR" ||
                csvFile.error === "INVALID_EMAILS") && (
                <TruncatedTextWithTooltip
                  className={styles.uploadFileName}
                  content={csvFile.fileName}
                  maxWidth={350}
                  placement="top"
                />
              )}
            </p>
            <CSVReader
              onChange={(csv) => {
                setCsvFile({
                  ...csvFile,
                  ...csv,
                });
              }}
            />
          </>
        )}
      </Modal.Body>
      {!!csvFile.error && csvFile.error !== "INVALID_ROWS" && (
        <div className={styles.errorTextWrapper}>
          <i className={cx(styles.icon, "cleverstory-warning")} />
          <p className={styles.errorText}>
            {csvFile.error === "MAX_ROWS_ERROR"
              ? "Your file contains more than 100 rows"
              : csvFile.error === "FILE_TYPE_ERROR"
              ? "Invalid file type"
              : csvFile.error === "INVALID_EMAILS"
              ? "Your file contains invalid emails"
              : ""}
          </p>
        </div>
      )}
      <Modal.Footer className={styles.csvUploadModalFooterWrapper}>
        {!isUploaded && (
          <p className={styles.footerText}>
            Download csv format template for uploading multiple contacts —
            <a
              href="https://d2uav5q06z9nv6.cloudfront.net/public/cleverstory/csv/contact.csv"
              target="_blank"
              download
            >
              <span> Download Sample csv</span>{" "}
            </a>
          </p>
        )}
        <div className={styles.footerBlock}>
          {isUploaded && isMoreThanHalf && (
            <p className={styles.footerImportText}>
              {creating ? "Importing " : "Import "}
              <span ref={uploadedCountRef}>{filteredList.length}</span> of{" "}
              <span>{csvFile.totalRowsCount}</span> rows
            </p>
          )}
          {isUploaded && !isMoreThanHalf && (
            <div className={styles.errorTextWrapper} style={{ margin: "0px" }}>
              <i className={cx(styles.icon, "cleverstory-warning")} />
              <p className={styles.errorText}>
                {"More than 50% of the rows are invalid."}
              </p>
            </div>
          )}
          <div className={cx(styles.csvUploadModalFooter)}>
            <Button
              disabled={creating}
              size="sm"
              variant="secondary"
              onClick={onClose}
              style={{ borderColor: "#E0E0E0" }}
            >
              Cancel
            </Button>
            <Button
              disabled={creating || csvFile.error || !isMoreThanHalf}
              size="sm"
              variant="primary"
              onClick={() => handleImport(Math.floor(filteredList.length / 25 < 0 ? 0 : filteredList.length / 25))}
            >
              {creating ? "Importing..." : "Import"}
            </Button>
          </div>
        </div>
      </Modal.Footer>
    </Modal>
  );
}

function CSVReader({ onChange }) {
  const inputRef = useRef(null);

  const validateEmail = (email) => {
    if (!email || typeof email !== "string") return false;

    const regex = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@(([a-zA-Z0-9]+(-[a-zA-Z0-9]+)*\.)+[a-zA-Z]{2,})$/;
    const domain = email?.split?.("@")?.[1]?.toLowerCase();

    return regex.test(email) && !publicDomains.includes(domain);
  };

  const handleFileChange = (e) => {
    try {
      const file = e.target.files[0];
      if (!file) return;

      if (file.type !== "text/csv") {
        onChange({
          error: "FILE_TYPE_ERROR",
          fileName: file.name || "",
        });
        return;
      }

      const reader = new FileReader();
      reader.onload = (event) => {
        try {
          const text = event.target.result;
          const rows = text.trim().split("\n");
          const headers = rows[0].split(",");

          let data = rows.slice(1).map((row) => {
            const values = row.split(",");
            const obj = {};

            headers.forEach((header, i) => {
              const key =
                headerMap[header.trim()?.toLowerCase()] ||
                header.trim()?.toLowerCase(); // fallback to original if not mapped
              obj[key] = values[i]?.trim() || "";
            });

            return obj;
          });
          data = _.uniqBy(data, "email");

          if(data.length === 1) {
            const email = data[0]["email"]?.trim();
            if (!email || !validateEmail(email)) {
              onChange({
                list: [],
                totalRowsCount: 0,
                error: "INVALID_EMAILS",
                fileName: file.name || "",
              });
              return;
            }
          }

          let totalRowsCount = data.length;

          if (data.length > 100) {
            onChange({
              error: "MAX_ROWS_ERROR",
              fileName: file.name || "",
            });
            return;
          }
          const list = data
          .filter((row) => {
            if (
              row["email"]?.trim() ||
              row["firstName"]?.trim() ||
              row["lastName"]?.trim()
            ) {
              return true;
            }

            totalRowsCount--;
          })
          .map((row) => {
            const email = row["email"]?.trim();
            if (!email) {
              row["email"] = row["firstName"] || row["lastName"];
            }

            if (!validateEmail(email)) {
              return {
                ...row,
                emailError: true,
              };
            }

            if (!row["firstName"]) {
              const parts = row["email"]?.split(/[^a-zA-Z0-9]+/); // split by non-alphanumeric chars
              row["firstName"] = parts?.[0] || "";
            }

            return row;
          });


          let request = {
            list,
            totalRowsCount,
            fileName: file.name || "",
            error: list.length <= 0 ? "INVALID_EMAILS" : "",
          };

          onChange(request);
        } catch (error) {
          console.log("Error processing CSV file:", error);
        }
      };
      reader.onerror = (error) => {
        console.error("Error reading file:", error);
      };

      reader.readAsText(file);
    } catch (e) {
      console.error("Error reading file:", e);
    }
  };

  const handleUploadClick = () => {
    inputRef.current?.click();
  };

  return (
    <label htmlFor="csv" className={styles.uploadWrapper}>
      <input
        id="csv"
        type="file"
        accept=".csv"
        hidden
        ref={inputRef}
        onChange={handleFileChange}
      />
      <Button
        variant="primary"
        className={styles.uploadButton}
        onClick={handleUploadClick}
      >
        <i className={cx(styles.icon, "cleverstory-upload")} />
        <span>Upload</span>
      </Button>
    </label>
  );
}

export default CsvUpload;
